import { Api } from "services/api/api.service";
import { err, isOk, ok } from "utils/result";
import type { Result } from "utils/result";
import type { TimeReportsV2Dispatch } from "./timeReportsV2Store";
import {
  timeReportsV2InitState,
  type TimeReportsV2GroupByOption,
  type TimeReportsV2ListQuery,
  type TimeReportsV2Preferences,
  type TimeReportsV2SummaryQuery,
} from "./timeReportsV2Store.reducer";

let listRequestCounter = 0;
let summaryRequestCounter = 0;
let countRequestCounter = 0;
const inFlightListRequests = new Map<string, Promise<void>>();
const inFlightSummaryRequests = new Map<string, Promise<void>>();

const timeReportsV2PreferencesStorageKey = (companyId: number): string =>
  `PERSISTED::TimeReportsV2Preferences::${companyId}`;

const hasStringGroupByOption = (
  value: object,
): value is { groupByOption: string } =>
  typeof Reflect.get(value, "groupByOption") === "string";

const isGroupByOption = (value: string): value is TimeReportsV2GroupByOption =>
  value === "person" || value === "team";

const parseTimeReportsV2Preferences = (
  raw: string,
): Result<TimeReportsV2Preferences, "invalid-preferences"> => {
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return err("invalid-preferences");
    }
    if (!hasStringGroupByOption(parsed)) {
      return err("invalid-preferences");
    }

    const groupByOption = parsed.groupByOption;
    if (!isGroupByOption(groupByOption)) {
      return err("invalid-preferences");
    }

    return ok({ groupByOption });
  } catch {
    return err("invalid-preferences");
  }
};

const serializeNumberList = (values: number[]): string => values.join(",");

const toListRequestKey = (
  companyId: number,
  query: TimeReportsV2ListQuery,
  page: number,
  append: boolean,
): string => {
  const filtersKey = query.filters?.join(",") ?? "";
  return [
    companyId,
    query.fromDate,
    query.toDate,
    page,
    query.pageSize,
    query.sortBy,
    filtersKey,
    serializeNumberList(query.workGroupIds),
    serializeNumberList(query.workerIds),
    append ? "append" : "replace",
  ].join("|");
};

const toSummaryRequestKey = (
  companyId: number,
  query: TimeReportsV2SummaryQuery,
): string => {
  return [
    companyId,
    query.fromDate,
    query.toDate,
    serializeNumberList(query.workGroupIds),
    serializeNumberList(query.workerIds),
  ].join("|");
};

const fetchList =
  (dispatch: TimeReportsV2Dispatch) =>
  async (
    companyId: number,
    query: TimeReportsV2ListQuery,
    page: number,
    append: boolean,
  ): Promise<void> => {
    const requestKey = toListRequestKey(companyId, query, page, append);
    const inFlightRequest = inFlightListRequests.get(requestKey);
    if (inFlightRequest) {
      return inFlightRequest;
    }

    const requestId = (listRequestCounter += 1);
    dispatch({ type: "SET_ACTIVE_LIST_REQUEST_ID", payload: requestId });
    dispatch({
      type: "SET_LIST_LOADING",
      payload: { value: page === 0, requestId },
    });
    dispatch({
      type: "SET_LIST_LOADING_MORE",
      payload: { value: page > 0, requestId },
    });
    dispatch({ type: "SET_LIST_REQUEST_ERROR", payload: null });
    if (page === 0 && !append) {
      dispatch({
        type: "SET_LIST",
        payload: {
          data: null,
          requestId,
          append: false,
          page: 0,
          pageSize: query.pageSize,
        },
      });
    }

    const request = (async (): Promise<void> => {
      try {
        const response = await Api().company.timeReports.getTimeReportsV2(
          companyId,
          query.fromDate,
          query.toDate,
          page,
          query.pageSize,
          query.sortBy,
          query.filters,
          query.workGroupIds,
          query.workerIds,
        );
        dispatch({
          type: "SET_LIST",
          payload: {
            data: response.data,
            requestId,
            append,
            page,
            pageSize: query.pageSize,
          },
        });
      } catch (error) {
        dispatch({
          type: "SET_LIST_REQUEST_ERROR",
          payload: {
            message: "Could not fetch time reports",
            err: error instanceof Error ? error : new Error("Unknown error"),
          },
        });
      } finally {
        dispatch({
          type: "SET_LIST_LOADING",
          payload: { value: false, requestId },
        });
        dispatch({
          type: "SET_LIST_LOADING_MORE",
          payload: { value: false, requestId },
        });
        inFlightListRequests.delete(requestKey);
      }
    })();

    inFlightListRequests.set(requestKey, request);
    await request;
  };

const fetchSummary =
  (dispatch: TimeReportsV2Dispatch) =>
  async (
    companyId: number,
    query: TimeReportsV2SummaryQuery,
  ): Promise<void> => {
    const requestKey = toSummaryRequestKey(companyId, query);
    const inFlightRequest = inFlightSummaryRequests.get(requestKey);
    if (inFlightRequest) {
      return inFlightRequest;
    }

    const requestId = (summaryRequestCounter += 1);
    dispatch({ type: "SET_ACTIVE_SUMMARY_REQUEST_ID", payload: requestId });
    dispatch({
      type: "SET_SUMMARY_LOADING",
      payload: { value: true, requestId },
    });
    dispatch({ type: "SET_SUMMARY_REQUEST_ERROR", payload: null });

    const request = (async (): Promise<void> => {
      try {
        const response =
          await Api().company.timeReports.getTimeReportsV2Summary(
            companyId,
            query.fromDate,
            query.toDate,
            query.workGroupIds,
            query.workerIds,
          );
        dispatch({
          type: "SET_SUMMARY",
          payload: { data: response.data, requestId },
        });
      } catch (error) {
        dispatch({
          type: "SET_SUMMARY_REQUEST_ERROR",
          payload: {
            message: "Could not refresh summary",
            err: error instanceof Error ? error : new Error("Unknown error"),
          },
        });
      } finally {
        dispatch({
          type: "SET_SUMMARY_LOADING",
          payload: { value: false, requestId },
        });
        inFlightSummaryRequests.delete(requestKey);
      }
    })();

    inFlightSummaryRequests.set(requestKey, request);
    await request;
  };

const refreshCount =
  (dispatch: TimeReportsV2Dispatch) =>
  async (companyId: number): Promise<void> => {
    const requestId = (countRequestCounter += 1);
    dispatch({ type: "SET_ACTIVE_COUNT_REQUEST_ID", payload: requestId });
    dispatch({
      type: "SET_COUNT_LOADING",
      payload: { value: true, requestId },
    });
    dispatch({ type: "SET_COUNT_REQUEST_ERROR", payload: null });

    try {
      const response =
        await Api().company.timeReports.getTimeReportV2Count(companyId);
      dispatch({
        type: "SET_COUNT",
        payload: { value: response.data.value, requestId },
      });
    } catch (error) {
      dispatch({
        type: "SET_COUNT_REQUEST_ERROR",
        payload: {
          message: "Could not refresh count",
          err: error instanceof Error ? error : new Error("Unknown error"),
        },
      });
    }

    dispatch({
      type: "SET_COUNT_LOADING",
      payload: { value: false, requestId },
    });
  };

const loadTimeReportsV2Preferences =
  (dispatch: TimeReportsV2Dispatch) =>
  (companyId: number): void => {
    dispatch({ type: "SET_PREFERENCES_REQUEST_ERROR", payload: null });
    const raw = localStorage.getItem(
      timeReportsV2PreferencesStorageKey(companyId),
    );
    if (!raw) return;
    const parsed = parseTimeReportsV2Preferences(raw);
    if (!isOk(parsed)) {
      dispatch({
        type: "SET_PREFERENCES_REQUEST_ERROR",
        payload: {
          message: "Could not load preferences",
          err: new Error("Could not parse stored preferences"),
        },
      });
      const fallback = timeReportsV2InitState.preferences;
      dispatch({ type: "SET_PREFERENCES", payload: fallback });
      try {
        localStorage.setItem(
          timeReportsV2PreferencesStorageKey(companyId),
          JSON.stringify(fallback),
        );
      } catch {
        dispatch({
          type: "SET_PREFERENCES_REQUEST_ERROR",
          payload: {
            message: "Could not load preferences",
            err: new Error("Could not write fallback preferences"),
          },
        });
        localStorage.removeItem(timeReportsV2PreferencesStorageKey(companyId));
      }
      return;
    }
    dispatch({ type: "SET_PREFERENCES", payload: parsed.value });
  };

const persistTimeReportsV2Preferences =
  (dispatch: TimeReportsV2Dispatch) =>
  (companyId: number, preferences: TimeReportsV2Preferences): void => {
    dispatch({ type: "SET_PREFERENCES_REQUEST_ERROR", payload: null });
    try {
      localStorage.setItem(
        timeReportsV2PreferencesStorageKey(companyId),
        JSON.stringify(preferences),
      );
    } catch {
      dispatch({
        type: "SET_PREFERENCES_REQUEST_ERROR",
        payload: {
          message: "Could not save preferences",
          err: new Error("Could not persist preferences to localStorage"),
        },
      });
      return;
    }
  };

export const timeReportsV2Actions = (dispatch: TimeReportsV2Dispatch) => ({
  fetchList: fetchList(dispatch),
  fetchSummary: fetchSummary(dispatch),
  refreshCount: refreshCount(dispatch),
  resetList: () => dispatch({ type: "RESET_LIST" }),
  setListQuery: (query: TimeReportsV2ListQuery | null) =>
    dispatch({ type: "SET_LIST_QUERY", payload: query }),
  setSummaryQuery: (query: TimeReportsV2SummaryQuery | null) =>
    dispatch({ type: "SET_SUMMARY_QUERY", payload: query }),
  setPreferences: (preferences: TimeReportsV2Preferences) =>
    dispatch({ type: "SET_PREFERENCES", payload: preferences }),
  setGroupByOption: (groupByOption: TimeReportsV2GroupByOption) =>
    dispatch({
      type: "SET_PREFERENCES",
      payload: { groupByOption },
    }),
  loadTimeReportsV2Preferences: loadTimeReportsV2Preferences(dispatch),
  persistTimeReportsV2Preferences: persistTimeReportsV2Preferences(dispatch),
  refreshAll: async (
    companyId: number,
    queries: {
      listQuery: TimeReportsV2ListQuery;
      summaryQuery: TimeReportsV2SummaryQuery;
    },
  ): Promise<void> => {
    dispatch({ type: "SET_LIST_QUERY", payload: queries.listQuery });
    dispatch({ type: "SET_SUMMARY_QUERY", payload: queries.summaryQuery });
    await Promise.all([
      fetchList(dispatch)(companyId, queries.listQuery, 0, false),
      fetchSummary(dispatch)(companyId, queries.summaryQuery),
      refreshCount(dispatch)(companyId),
    ]);
  },
});
